if (!customElements.get('build-your-moment')) {
  customElements.define('build-your-moment', class extends HTMLElement {
    connectedCallback() {
      if (!this.cartUnsubscribe && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
          if (event.source === 'build-your-moment') return;
          const count = event.cartData?.item_count;
          if (Number.isInteger(count) && count >= 0) this.querySelector('[data-cart-count]').textContent = `(${count})`;
          else this.refreshCart().catch(() => {});
        });
      }
      this.initAnchorNavigation();
      if (this.ready) return;
      this.ready = true; this.busy = false; this.eventFilter = 'all';
      this.heroIndex = 0;
      this.querySelector('.moment-carousel')?.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); this.showHero(this.heroIndex + (event.key === 'ArrowRight' ? 1 : -1));
      });
      const zoom = this.querySelector('[data-image-zoom]');
      zoom?.addEventListener('keydown', event => {
        if (['ArrowLeft', 'ArrowRight'].includes(event.key)) { event.preventDefault(); this.showZoomPhoto((this.zoomIndex || 0) + (event.key === 'ArrowRight' ? 1 : -1)); }
      });
      zoom?.addEventListener('close', () => this.zoomOpener?.focus({preventScroll:true}));
      this.repairPreviewImages();
      this.querySelectorAll('[data-moment-product]').forEach(row => this.initOptions(row));
      this.addEventListener('click', event => this.onClick(event));
      this.addEventListener('change', event => {
        if (event.target.matches('[data-style]')) this.filterLooks();
        if (event.target.matches('[data-product-option]')) this.resolveOptions(event.target.closest('[data-moment-product]'), true);
        if (event.target.matches('[data-variant]')) this.updateVariant(event.target.closest('[data-moment-product]'));
        this.updateTotal(event.target.closest('dialog'));
        this.updateSpaceBudget(event.target.closest('.moment-space-card'));
      });
      this.addEventListener('input', event => {
        this.updateTotal(event.target.closest('dialog'));
        this.updateSpaceBudget(event.target.closest('.moment-space-card'));
      });
      this.querySelectorAll('dialog').forEach(dialog => dialog.addEventListener('click', event => { if(event.target === dialog) dialog.close(); }));
      this.querySelectorAll('[data-look-dialog]').forEach(dialog => this.initSceneIncludes(dialog));
      this.querySelectorAll('[data-look-dialog], .moment-space-card').forEach(dialog => this.initPlannerSummary(dialog));
      this.filterLooks(); this.filterStep('structure');
      this.querySelectorAll('[data-moment-product]').forEach(row => this.updateVariant(row));
      this.querySelectorAll('dialog').forEach(dialog => this.updateTotal(dialog));
      this.querySelectorAll('.moment-space-card').forEach(card => this.updateSpaceBudget(card));
    }
    disconnectedCallback() {
      this.anchorCleanup?.();
      this.anchorCleanup = null;
      this.cartUnsubscribe?.();
      this.cartUnsubscribe = null;
    }
    initAnchorNavigation() {
      const nav = this.querySelector('[data-anchor-nav]');
      if (!nav || this.anchorCleanup) return;
      const links = [...nav.querySelectorAll('[data-anchor-link]')];
      const targets = links.map(link => this.querySelector(link.getAttribute('href')));
      const headerElements = [...document.querySelectorAll('.section-header, .section-header .header-wrapper, #magic-grad-banner')];
      const visibleHeaderBottom = () => headerElements.reduce((bottom, element) => {
        const style = getComputedStyle(element);
        const rect = element.getBoundingClientRect();
        if (!['fixed', 'sticky'].includes(style.position) || style.visibility === 'hidden' || rect.height === 0 || rect.bottom <= 0) return bottom;
        return Math.max(bottom, rect.bottom);
      }, 0);
      let frame = 0;
      const update = () => {
        frame = 0;
        const headerBottom = visibleHeaderBottom();
        nav.style.top = `${headerBottom}px`;
        const spacer = this.querySelector('[data-anchor-spacer]');
        if (spacer) spacer.style.height = `${nav.getBoundingClientRect().height}px`;

        const offset = headerBottom + nav.getBoundingClientRect().height + 16;
        this.style.setProperty('--moment-anchor-offset', `${offset}px`);
        let active = 0;
        targets.forEach((target, i) => { if (target && target.getBoundingClientRect().top <= offset + 8) active = i; });
        links.forEach((link, i) => {
          if (i === active) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
        const current = links[active];
        if (current) {
          const list = current.parentElement;
          const left = current.offsetLeft - list.offsetLeft;
          if (left < list.scrollLeft) list.scrollLeft = left;
          else if (left + current.offsetWidth > list.scrollLeft + list.clientWidth) list.scrollLeft = left + current.offsetWidth - list.clientWidth;
        }
      };
      const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
      const click = event => {
        const link = event.target.closest('[data-anchor-link]');
        if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
        const target = targets[links.indexOf(link)];
        if (!target) return;
        event.preventDefault();
        update();
        // Reserve the full sticky header so it can reappear during upward scrolling.
        const header = document.querySelector('.section-header');
        const reservedHeader = Math.max(visibleHeaderBottom(), header?.classList.contains('shopify-section-header-sticky') ? header.getBoundingClientRect().height : 0);
        const offset = reservedHeader + nav.getBoundingClientRect().height + 16;
        const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - offset);
        window.scrollTo({top, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
        history.replaceState(null, '', link.getAttribute('href'));
      };
      nav.addEventListener('click', click);
      window.addEventListener('scroll', schedule, {passive: true});
      window.addEventListener('resize', schedule);
      const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
      observer?.observe(nav);
      headerElements.forEach(header => observer?.observe(header));
      this.anchorCleanup = () => {
        nav.removeEventListener('click', click);
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', schedule);
        observer?.disconnect();
        if (frame) cancelAnimationFrame(frame);
      };
      update();
    }
    repairPreviewImages() {
      // Shopify's local /cdn/shop/files proxy may fail while the public CDN works.
      // Limit the workaround to this local preview; never rewrite production images.
      if (!['localhost', '127.0.0.1', '[::1]'].includes(location.hostname)) return;
      const origin = this.dataset.storeOrigin;
      if (!origin || !origin.startsWith('https://')) return;
      const absolute = value => {
        const url = new URL(value, location.href);
        return url.origin === location.origin && url.pathname.startsWith('/cdn/shop/files/') ? origin + url.pathname + url.search : value;
      };
      document.querySelectorAll('img').forEach(img => {
        if (img.getAttribute('srcset')) img.setAttribute('srcset', img.getAttribute('srcset').split(',').map(part => {
          const [url, ...size] = part.trim().split(/\s+/); return [absolute(url), ...size].join(' ');
        }).join(', '));
        if (img.getAttribute('src')) img.setAttribute('src', absolute(img.getAttribute('src')));
      });
    }
    initOptions(row) {
      const fields = row.querySelector('[data-option-fields]');
      if (!fields || !fields.querySelector('select')) return;
      fields.hidden = false;
      row.querySelector('[data-combined-options]').hidden = true;
      row.querySelectorAll('[data-product-option]').forEach(select => {
        if (select.options.length === 2) select.value = select.options[1].value;
      });
      if (row.dataset.preset) {
        const preset = JSON.parse(row.dataset.preset);
        const normalise = value => value.toLowerCase().replace(/\s+/g, '');
        const candidates = [...row.querySelector('[data-variant]').options].filter(option => {
          if (!option.value) return false;
          if (preset.id) return option.value === preset.id;
          const values = JSON.parse(option.dataset.options);
          if (preset.options) return values.length === preset.options.length && values.every((value, i) => normalise(value) === normalise(preset.options[i]));
          return preset.tokens?.every(token => values.some(value => normalise(value).includes(normalise(token))));
        });
        if (candidates.length === 1) {
          row.dataset.sceneQuotePrice = candidates[0].dataset.price;
          const values = JSON.parse(candidates[0].dataset.options);
          row.querySelectorAll('[data-product-option]').forEach((select, i) => select.value = values[i]);
        } else {
          row.querySelectorAll('[data-product-option]').forEach(select => select.value = '');
        }
      }
      this.resolveOptions(row, Boolean(row.dataset.preset));
    }
    resolveOptions(row, includeSelected = false) {
      const choices = [...row.querySelectorAll('[data-product-option]')].map(select => select.value);
      const variants = [...row.querySelector('[data-variant]').options].filter(option => option.value);
      const match = choices.every(Boolean) && variants.find(option => JSON.parse(option.dataset.options).every((value, i) => value === choices[i]));
      row.querySelector('[data-variant]').value = match ? match.value : '';
      row.dataset.unavailable = String(choices.every(Boolean) && (!match || match.disabled));
      this.updateVariant(row);
      const include = row.querySelector('[data-include]');
      if (includeSelected && include && !include.disabled) include.checked = true;
    }
    async copyDiscountCode(button) {
      const status = this.querySelector('[data-copy-status]');
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(button.dataset.copyCode);
        } else {
          const field = document.createElement('textarea');
          field.value = button.dataset.copyCode;
          field.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
          document.body.appendChild(field);
          try {
            field.select();
            if (!document.execCommand('copy')) throw new Error('Copy failed');
          } finally { field.remove(); button.focus({preventScroll:true}); }
        }
        if (status) status.textContent = 'Copied!';
      } catch (error) {
        if (status) status.textContent = 'Please copy the code FB10 manually.';
      }
    }
    onClick(event) {
      const copyCode = event.target.closest('[data-copy-code]');
      if (copyCode) { this.copyDiscountCode(copyCode); return; }

      const button = event.target.closest('button'); if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-image-zoom-open')) {
        const gallery = button.closest('.moment-space-gallery');
        this.zoomPhotos = [...gallery.querySelectorAll('[data-review-slide] img')].map(img => ({src:img.currentSrc || img.src, alt:img.alt}));
        const selected = button.closest('[data-review-slide]').querySelector('img');
        this.zoomIndex = [...gallery.querySelectorAll('[data-review-slide] img')].indexOf(selected);
        this.zoomOpener = button;
        this.showZoomPhoto(this.zoomIndex);
        this.querySelector('[data-image-zoom]').showModal();
        return;
      }
      if (button.hasAttribute('data-zoom-direction')) { this.showZoomPhoto(this.zoomIndex + Number(button.dataset.zoomDirection)); return; }
      if (button.hasAttribute('data-review-direction')) {
        const carousel = button.closest('[data-review-carousel]');
        const slides = [...carousel.querySelectorAll('[data-review-slide]')];
        const index = (Number(carousel.dataset.index || 0) + Number(button.dataset.reviewDirection) + slides.length) % slides.length;
        slides.forEach((slide, i) => { slide.hidden = i !== index; });
        carousel.dataset.index = String(index);
        carousel.querySelector('[data-review-position]').textContent = `${index + 1} / ${slides.length}`;
        return;
      }
      if (button.hasAttribute('data-download-list')) { this.downloadShoppingList(button.closest('dialog')); return; }
      if (button.hasAttribute('data-add-space')) { const card = button.closest('.moment-space-card'); this.add([...card.querySelectorAll('[data-moment-product]')].filter(row => row.querySelector('[data-include]')?.checked), button); }
      if (button.hasAttribute('data-hero-prev')) this.showHero(this.heroIndex - 1);
      if (button.hasAttribute('data-hero-next')) this.showHero(this.heroIndex + 1);
      if (button.hasAttribute('data-open-look')) {
        const dialog = [...this.querySelectorAll('[data-look-dialog]')].find(d => d.dataset.lookDialog === button.dataset.openLook);
        if (!dialog) return; dialog.momentOpener = button; dialog.showModal();
        if (button.dataset.slot) { const row = dialog.querySelector(`[data-slot="${button.dataset.slot}"]`); row?.scrollIntoView({block:'nearest'}); row?.querySelector('select,a')?.focus({preventScroll:true}); }
      }
      if (button.hasAttribute('data-close')) button.closest('dialog').close();
      if (button.hasAttribute('data-occasion')) { this.eventFilter = button.dataset.occasion; this.filterLooks(); }
      if (button.hasAttribute('data-reset')) { this.eventFilter = 'all'; this.querySelector('[data-style]').value = 'all'; this.filterLooks(); }
      if (button.hasAttribute('data-step')) this.filterStep(button.dataset.step);
      if (button.hasAttribute('data-next-step')) {
        const steps = ['structure', 'backdrop', 'decor', 'accessories'];
        const next = steps[steps.indexOf(this.currentStep) + 1];
        if (next) { this.filterStep(next); this.querySelector(`button[data-step="${next}"]`)?.focus(); }
      }
      if (button.hasAttribute('data-add-single')) this.add([button.closest('[data-moment-product]')], button);
      if (button.hasAttribute('data-add-selected')) this.add([...button.closest('dialog').querySelectorAll('[data-moment-product]')].filter(row => row.querySelector('[data-include]')?.checked), button);
    }
    showZoomPhoto(index) {
      if (!this.zoomPhotos?.length) return;
      this.zoomIndex = (index + this.zoomPhotos.length) % this.zoomPhotos.length;
      const zoom = this.querySelector('[data-image-zoom]'), image = zoom.querySelector('[data-zoom-image]');
      image.src = this.zoomPhotos[this.zoomIndex].src; image.alt = this.zoomPhotos[this.zoomIndex].alt;
      zoom.querySelector('[data-zoom-position]').textContent = `${this.zoomIndex + 1} / ${this.zoomPhotos.length}`;
      zoom.querySelectorAll('[data-zoom-direction]').forEach(button => button.hidden = this.zoomPhotos.length < 2);
    }
    showHero(index) {
      const slides = [...this.querySelectorAll('[data-hero-slide]')];
      if (!slides.length) return;
      const active = document.activeElement;
      const focusInSlide = slides.some(slide => slide.contains(active));
      this.heroIndex = (index % slides.length + slides.length) % slides.length;
      slides.forEach((slide, i) => slide.hidden = i !== this.heroIndex);
      const status = this.querySelector('[data-hero-status]');
      if (status) status.textContent = `${this.heroIndex + 1} / ${slides.length} · ${slides[this.heroIndex].dataset.slideTitle}`;
      if (focusInSlide) slides[this.heroIndex].querySelector('[data-open-look]')?.focus({preventScroll:true});
    }
    filterLooks() {
      const style = this.querySelector('[data-style]').value; let visible = 0;
      const cards = [...this.querySelectorAll('[data-look-card]')];
      this.querySelectorAll('[data-style] option').forEach(option => {
        option.disabled = option.value !== 'all' && !cards.some(card => card.dataset.style === option.value);
      });
      this.querySelectorAll('[data-look-card]').forEach(card => {
        const events = card.dataset.events.split(',').map(s=>s.trim());
        card.hidden = !(this.eventFilter === 'all' || events.includes(this.eventFilter)) || !(style === 'all' || style === card.dataset.style);
        if (!card.hidden) visible++;
      });
      this.querySelectorAll('[data-occasion]').forEach(b=>b.setAttribute('aria-pressed', String(b.dataset.occasion === this.eventFilter)));
      this.querySelector('[data-no-looks]').hidden = visible > 0;
      this.querySelector('[data-look-count]').textContent = `${visible} look${visible === 1 ? '' : 's'}`;
    }
    filterStep(step) {
      this.currentStep = step;
      let count=0;
      this.querySelectorAll('[data-builder-card]').forEach(c=>{c.hidden=c.dataset.step !== step;if(!c.hidden && c.querySelector('[data-moment-product]')) count++;});
      this.querySelectorAll('button[data-step]').forEach(b=>b.setAttribute('aria-pressed',String(b.dataset.step===step)));
      this.querySelector('[data-no-products]').hidden = count>0;
      const nextButton = this.querySelector('[data-next-step]');
      const nextLabels = {structure:'Next: choose a backdrop →',backdrop:'Next: add decor →',decor:'Next: finishing touches →'};
      if (nextButton) { nextButton.hidden = !nextLabels[step]; nextButton.textContent = nextLabels[step] || ''; }
    }
    updateVariant(row) {
      const select = row.querySelector('[data-variant]');
      if (!select) return;
      const option = select.selectedOptions[0], custom = option?.dataset.custom === 'true';
      const note = row.querySelector('[data-custom-note]');
      if (note) {
        note.hidden = !custom || option.disabled;
        const link = note.querySelector('[data-custom-link]');
        if (custom && link) { const url = new URL(link.href); url.searchParams.set('variant', option.value); link.href = url.href; }
      }
      row.querySelectorAll?.('[data-details-link]').forEach(link => {
        if (!link.href) return;
        const url = new URL(link.href);
        if (option?.value) url.searchParams.set('variant', option.value);
        else url.searchParams.delete('variant');
        link.href = url.href;
      });
      const disabled = this.busy || !option?.value || option.disabled || custom;
      row.querySelector('[data-add-single]').disabled = disabled;
      const button = row.querySelector('[data-add-single]');
      button.hidden = custom && !option.disabled;
      if (!this.busy) button.textContent = custom ? 'Custom options required' : row.dataset?.unavailable === 'true' || option?.disabled ? 'Unavailable' : !option?.value ? 'Choose options above' : 'Add to cart';
      const price = row.querySelector('[data-product-price]');
      if (price) price.textContent = option?.value ? option.dataset.money : price.dataset.startingPrice;
      const hint = row.querySelector('[data-option-hint]');
      if (hint) hint.textContent = custom ? 'Personalise this item using the link below.' : row.dataset.unavailable === 'true' ? 'This combination is unavailable. Choose another option.' : !option?.value ? 'Choose each option above to continue.' : 'Price shown is per item. Adjust quantity below.';
      const include = row.querySelector('[data-include]');
      if (include) { const selectionDisabled = this.busy || !option?.value || option.disabled; include.disabled = selectionDisabled; if (selectionDisabled) include.checked = false; }
    }
    updateSpaceBudget(card) {
      if (!card) return;
      const budget = card.querySelector('[data-space-budget]');
      const label = card.querySelector('[data-space-budget-label]');
      if (!budget || !label) return;
      const allSpaceRows = [...card.querySelectorAll('[data-moment-product]')];
      const customCount = allSpaceRows.filter(row => row.querySelector('[data-variant]')?.selectedOptions[0]?.dataset.custom === 'true' || !row.querySelector('[data-variant]')).length;
      const customNote = card.querySelector('[data-space-custom-note]');
      if (customNote) { customNote.hidden = customCount === 0; customNote.textContent = `${customCount} custom item${customCount === 1 ? '' : 's'} priced separately`; }
      card.querySelectorAll('[data-space-size-index]').forEach(detail => {
        const row = allSpaceRows[Number(detail.dataset.spaceSizeIndex) - 1];
        const option = row?.querySelector('[data-variant]')?.selectedOptions[0];
        let sizes = [];
        try { sizes = JSON.parse(option?.dataset.options || '[]').filter(value => /\d/.test(value)); } catch {}
        detail.textContent = sizes.length ? sizes.join(' · ') : detail.dataset.sizeDefault;
      });
      const rows = allSpaceRows.filter(row => row.querySelector('[data-include]')?.checked);
      let total = 0, valid = true;
      try { rows.forEach(row => { const item = this.readRow(row, true); total += item.price * item.quantity; }); } catch { valid = false; }
      if (!rows.length) { budget.textContent = budget.dataset.startingBudget; label.textContent = 'Product options from'; }
      else { budget.textContent = valid ? this.money(total) : 'Check selected options'; label.textContent = 'Selected items total'; }
      const add = card.querySelector('[data-add-space]'); if (add) add.disabled = this.busy || !valid || !rows.length || rows.some(row => row.querySelector('[data-variant]')?.selectedOptions[0]?.dataset.custom === 'true');
      this.updatePlannerSummary(card);
      const note = card.querySelector('[data-space-selection-note]');
      if (note) note.textContent = rows.length ? `${rows.length} product${rows.length === 1 ? '' : 's'} selected. Selected custom items are included at their listed prices and require personalisation on their product pages. Deselect custom items to add standard items here.` : 'Choose options to select items. Save custom products individually to your shopping list.';
    }
    initSceneIncludes(dialog) {
      const guidance = dialog.querySelector('.moment-scene-guidance');
      if (!guidance || guidance.querySelector('[data-scene-includes]')) return;
      const paragraphs = [...guidance.querySelectorAll('p')];
      const first = paragraphs[0];
      if (first) {
        const text = first.textContent.trim();
        // The opening sentence enumerates the same items now shown in the list.
        const split = text.indexOf('. ');
        if (split >= 0) first.textContent = text.slice(split + 2).replace(/^Quantity: 1 each\.\s*/, '');
        else first.remove();
      }
      const list = document.createElement('ul');
      list.className = 'moment-scene-includes'; list.dataset.sceneIncludes = '';
      const heading = guidance.querySelector('strong');
      if (heading) { heading.textContent = 'Included main pieces'; heading.after(list); }
      else guidance.prepend(list);
      const disclosure = document.createElement('details');
      disclosure.className = 'moment-includes-disclosure';
      disclosure.open = !window.matchMedia('(max-width:600px)').matches;
      const summary = document.createElement('summary');
      summary.className = 'moment-includes-toggle';
      summary.innerHTML = '<span>👉 Included main pieces</span><span class="moment-includes-state"><span class="moment-includes-expand">Expand +</span><span class="moment-includes-collapse">Collapse −</span></span>';
      disclosure.appendChild(summary);
      // Include both the product checklist and all explanatory paragraphs.
      [...guidance.childNodes].forEach(node => {
        if (node === heading) node.remove();
        else disclosure.appendChild(node);
      });
      guidance.appendChild(disclosure);
      this.updateSceneIncludes(dialog);
    }
    updateSceneIncludes(dialog) {
      const list = dialog.querySelector('[data-scene-includes]');
      if (!list) return;
      list.replaceChildren();
      dialog.querySelectorAll('[data-moment-product]').forEach(row => {
        const item = document.createElement('li');
        const option = row.querySelector('[data-variant]')?.selectedOptions[0];
        let specification = '';
        try { if (option?.value) specification = JSON.parse(option.dataset.options).join(' / '); } catch {}
        const quantity = row.querySelector('[data-quantity]')?.value || '1';
        const name = row.dataset.productTitle || 'Product';
        item.textContent = `✓ ${name}${specification ? ' · ' + specification : ''} ×${quantity}`;
        list.appendChild(item);
      });
    }
    initPlannerSummary(dialog) {
      if (dialog.querySelector('[data-planner-summary]')) return;
      const container = dialog.querySelector('.moment-dialog__products') || dialog.querySelector('.moment-space-purchase__body');
      if (!container) return;
      const panel = document.createElement('section');
      panel.className = 'moment-planner-summary'; panel.dataset.plannerSummary = '';
      panel.innerHTML = '<details class="moment-planner-detail"><summary class="moment-planner-toggle"><span>👉 Size & compatibility</span><span class="moment-planner-toggle-state"><span class="moment-planner-expand">Expand +</span><span class="moment-planner-collapse">Collapse −</span></span></summary><p data-planner-size></p></details><details class="moment-planner-detail"><summary class="moment-planner-toggle"><span>👉 Procurement checklist</span><span class="moment-planner-toggle-state"><span class="moment-planner-expand">Expand +</span><span class="moment-planner-collapse">Collapse −</span></span></summary><ul data-planner-list></ul><p class="moment-note">Only the products and quantities below are planned. Photo styling is not a complete package. Check the scene notes for exclusions.</p></details><details class="moment-planner-detail"><summary class="moment-planner-toggle"><span>👉 Budget breakdown</span><span class="moment-planner-toggle-state"><span class="moment-planner-expand">Expand +</span><span class="moment-planner-collapse">Collapse −</span></span></summary><p data-planner-budget></p><p class="moment-note">Free shipping &amp; taxes included. Prices are before discounts. Custom amounts are references until personalisation is confirmed. Balloons, props, installation and venue costs are excluded unless explicitly listed. FB10 excludes products with a strikethrough price; final eligibility is confirmed at checkout.</p></details>';
      const id = dialog.dataset.lookDialog || "";
      let size = 'Selected options below show product dimensions, not the complete installed footprint. Confirm the fabric and frame shape, width, height and attachment method before purchase. Allow additional space for bases, decor and guest access; total footprint is not yet confirmed.';
      if (id === 'look_winter') size = 'Aluminum arches: four 10×8ft stands, stand only. Round stand: 2m (6.5ft), stand only. Hedge wall: 8×8ft square. Oh Baby neon sign: 42×70cm. Confirm separate support and mounting requirements; the square hedge wall is not a fitted round cover. Balloon styling is separate.';
      else if (id.startsWith('pink-leopard-')) size = 'Panels: 64PCS / 8×8ft. Stand: 8×8FT. Nominal dimensions match; confirm panel mounting clips or ties and stand load suitability before installation. The complete footprint must include the stand bases.';
      else if (id.startsWith('white-dual-arch-')) size = 'Two separate Stand + Cover kits: 4×7ft and 3×6ft. These are individual kit sizes, not the combined display width. Confirm the spacing and base clearance for your venue; request white for both custom colours.';
      else if (id.startsWith('romantic-red-')) size = 'Heart: 8×8ft Stand + Ruched Cover. Plush covers: 60 / 80 / 100 / 120CM, one each. Covers do not include inflatable balls or a pump: match each cover to a compatible ball. Confirm custom red colours and allow clearance around the flower bases.';
      panel.querySelector('[data-planner-size]').textContent = size;
      container.before(panel);
    }
    updatePlannerSummary(dialog) {
      const panel = dialog.querySelector('[data-planner-summary]');
      if (!panel) return;
      const list = panel.querySelector('[data-planner-list]'); list.replaceChildren();
      let readyTotal = 0, customTotal = 0, pending = 0, customCount = 0;
      dialog.querySelectorAll('[data-moment-product]').forEach(row => {
        const option = row.querySelector('[data-variant]')?.selectedOptions[0];
        const quantityInput = row.querySelector('[data-quantity]');
        const qty = Number(quantityInput?.value || 1);
        const validQty = Number.isInteger(qty) && qty >= 1 && qty <= 99;
        const selected = row.querySelector('[data-include]')?.checked;
        const custom = option?.dataset.custom === 'true' || !row.querySelector('[data-variant]');
        let status = custom ? 'Personalisation required' : selected ? 'Selected for cart' : 'Not selected for cart';
        if (option?.disabled) status = 'Unavailable — choose another option';
        if (!validQty) status = 'Check quantity';
        let options = 'Options pending';
        try { if (option?.value) options = JSON.parse(option.dataset.options).join(' / '); } catch {}
        const item = document.createElement('li');
        item.textContent = `${row.dataset.productTitle} — ${options} · Qty ${validQty ? qty : '?'} · ${status}`;
        list.appendChild(item);
        if (custom) customCount++;
        const price = Number(option?.dataset.price);
        if (!option?.value || option.disabled || !validQty || !Number.isFinite(price)) pending++;
        else if (custom) customTotal += price * qty;
        else if (selected) readyTotal += price * qty;
      });
      panel.querySelector('[data-planner-budget]').textContent = `Selected ready-to-cart products: ${this.money(readyTotal)}. Custom products (${customCount}), known reference subtotal: ${this.money(customTotal)}${pending ? `; ${pending} item(s) still need options, availability or quantity confirmation` : ''}. Custom products are excluded from the ready-to-cart subtotal.`;
    }
    readRow(row, allowCustom = false) {
      const select=row.querySelector('[data-variant]'), qty=row.querySelector('[data-quantity]');
      const option=select?.selectedOptions[0];
      if (!option?.value || option.disabled) throw new Error('Please choose an available size or option for every selected item.');
      if (!allowCustom && option.dataset.custom === 'true') throw new Error('Please personalise this option on its product page before adding it.');
      if (!qty.checkValidity()) throw new Error('Please enter a whole quantity from 1 to 99.');
      return {id:option.value,quantity:Number(qty.value),price:Number(option.dataset.price)};
    }
    money(cents) { return new Intl.NumberFormat(document.documentElement.lang || 'en',{style:'currency',currency:this.dataset.currency || 'USD'}).format(cents/100); }
    updateTotal(dialog) {
      if (!dialog || dialog.hasAttribute?.('data-procurement') || dialog.hasAttribute?.('data-image-zoom')) return;
      const rows=[...dialog.querySelectorAll('[data-moment-product]')].filter(r=>r.querySelector('[data-include]')?.checked);
      let sum=0, valid=true;
      try { rows.forEach(r=>{const item=this.readRow(r, true);sum+=item.price*item.quantity;}); } catch {valid=false;}
      dialog.querySelector('[data-total]').textContent = valid ? this.money(sum) : 'Choose options';
      const customCount = [...dialog.querySelectorAll('[data-moment-product]')].filter(row => row.querySelector('[data-variant]')?.selectedOptions[0]?.dataset.custom === 'true').length;
      const selectedMessage = rows.length ? `${rows.length} product${rows.length===1?'':'s'} selected. Check sizes and quantities before adding.` : 'Select the pieces you need.';
      dialog.querySelector('[data-selection-note]').textContent = selectedMessage + (customCount ? ` ${customCount} custom item${customCount === 1 ? '' : 's'} require personalisation on their product pages. Their listed prices are included when selected. Save your selection to the shopping list; deselect custom items to add standard items here.` : '');
      dialog.querySelector('[data-add-selected]').disabled=this.busy || !valid || !rows.length || rows.some(row => row.querySelector('[data-variant]')?.selectedOptions[0]?.dataset.custom === 'true');
      this.updateSceneIncludes(dialog);
      this.updatePlannerSummary(dialog);
      const budget = [...this.querySelectorAll('[data-scene-budget]')].find(el => el.dataset.sceneBudget === dialog.dataset.lookDialog);
      if (budget) {
        const allRows = [...dialog.querySelectorAll('[data-moment-product]')];
        let complete = allRows.length === Number(dialog.dataset.expectedPieces), fullTotal = 0;
        // Quote the configured quantities, including custom options.
        // Purchase validation still requires personalization and availability.
        allRows.forEach(row => {
          const rawPrice = row.dataset.sceneQuotePrice;
          const price = Number(rawPrice);
          if (rawPrice === undefined || rawPrice === '' || !Number.isFinite(price)) complete = false;
          else fullTotal += price * Number(row.querySelector('[data-quantity]')?.defaultValue || 1);
        });
        budget.textContent = complete ? `Featured pieces: ${this.money(fullTotal)}` : 'Price to be confirmed';
      }

    }
    shoppingListCSV(dialog) {
      const rows = [...dialog.querySelectorAll('[data-moment-product]')].filter(row => row.querySelector('[data-include]')?.checked);
      if (!rows.length) throw new Error('Select at least one item to download your list.');
      const title = dialog.querySelector('h3').textContent.trim();
      const output = [['Scene', title], ['Currency', this.dataset.currency || 'USD'], ['Includes / excludes', dialog.dataset.shoppingNote || 'Only selected items are included. Styling is separate.'], [], ['Product', 'Selected option', 'Quantity', 'Unit price', 'Subtotal', 'Product URL']];
      let total = 0;
      rows.forEach(row => {
        const item = this.readRow(row, true), option = row.querySelector('[data-variant]').selectedOptions[0];
        const url = new URL(row.dataset.productUrl); url.searchParams.set('variant', item.id);
        let options; try { options = JSON.parse(option.dataset.options).join(' / '); } catch { options = option.textContent.trim(); }
        total += item.price * item.quantity;
        output.push([row.dataset.productTitle, options, item.quantity, (item.price / 100).toFixed(2), (item.price * item.quantity / 100).toFixed(2), url.href]);
      });
      output.push([], ['Selected items total', (total / 100).toFixed(2)], ['Note', 'Current product prices only. Free shipping & taxes included. Applicable discounts are calculated at checkout.']);
      const cell = value => {
        let text = String(value ?? '');
        if (/^[=+@\-\t\r]/.test(text)) text = "'" + text;
        return '"' + text.replace(/"/g, '""') + '"';
      };
      return '\uFEFF' + output.map(row => row.map(cell).join(',')).join('\r\n');
    }
    downloadShoppingList(dialog) {
      try {
        const csv = this.shoppingListCSV(dialog);
        const url = URL.createObjectURL(new Blob([csv], {type:'text/csv;charset=utf-8'}));
        const link = document.createElement('a'); link.href = url; link.download = 'ubackdrop-shopping-list.csv';
        document.body.appendChild(link); link.click(); link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        this.status('Shopping list downloaded. Prices may change before checkout.', false, dialog);
      } catch (error) { this.status(error.message, true, dialog); }
    }
    status(message, error=false, dialog=null) {
      const targets=[this.querySelector('[data-status]'), dialog?.querySelector('[data-dialog-status]')].filter(Boolean);
      targets.forEach(t=>{t.textContent=message;t.dataset.error=String(error);});
    }
    async refreshCart() {
      const res=await fetch(`${this.dataset.root}cart.js`,{headers:{Accept:'application/json'}});
      if(!res.ok) throw new Error('Cart refresh failed');
      const cart=await res.json();this.querySelector('[data-cart-count]').textContent=`(${cart.item_count})`;return cart;
    }
    async add(rows, button) {
      if(this.busy || !rows.length) return;
      const dialog=button.closest('dialog'); let items;
      try {
        const grouped=new Map(); rows.map(r=>this.readRow(r)).forEach(({id,quantity})=>{grouped.set(id,(grouped.get(id)||0)+quantity);});
        items=[...grouped].map(([id,quantity])=>({id,quantity}));
      } catch(error) {this.status(error.message,true,dialog);return;}
      this.busy=true;const old=button.textContent;button.textContent='Adding…';
      const buttons=[...this.querySelectorAll('[data-add-single],[data-add-selected]')];const previous=buttons.map(b=>b.disabled);buttons.forEach(b=>b.disabled=true);
      let added=false;
      try {
        const drawer=document.querySelector('cart-drawer');
        const sections=drawer?.getSectionsToRender?.().map(s=>s.id)||['cart-icon-bubble'];
        const response=await fetch(`${this.dataset.root}cart/add.js`,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items,sections,sections_url:window.location.pathname})});
        const data=await response.json();
        if(!response.ok || data.status) throw new Error(data.description || 'These items could not be added. Check your cart before trying again.');
        added=true;this.status('Added to your cart. Keep exploring or view your cart to check out.',false,dialog);
        try {
          await this.refreshCart();
          if(drawer && sections.every(id=>typeof data.sections?.[id]==='string')) {
            if(dialog?.open) dialog.close();drawer.setActiveElement(dialog?.momentOpener || button);drawer.classList.remove('is-empty');drawer.renderContents(data);
          } else if(data.sections?.['cart-icon-bubble']) {
            const parsed=new DOMParser().parseFromString(data.sections['cart-icon-bubble'],'text/html');
            const bubble=document.getElementById('cart-icon-bubble');if(bubble) bubble.innerHTML=parsed.querySelector('.shopify-section')?.innerHTML||bubble.innerHTML;
          }
          if(typeof publish==='function' && typeof PUB_SUB_EVENTS!=='undefined') publish(PUB_SUB_EVENTS.cartUpdate,{source:'build-your-moment',cartData:data});
        } catch {this.status('Items were added. Open your cart to confirm the latest quantities.',false,dialog);}
      } catch(error) {
        this.status(`${error.message} If the connection was interrupted, check your cart before adding again.`,true,dialog);
        try{await this.refreshCart();}catch{}
      } finally {
        this.busy=false;button.textContent=old;buttons.forEach((b,i)=>b.disabled=previous[i]);
        this.querySelectorAll('[data-moment-product]').forEach(row=>this.updateVariant(row));
        if(added && dialog) dialog.querySelectorAll('[data-include]').forEach(c=>c.checked=false);
        this.querySelectorAll('dialog').forEach(d=>this.updateTotal(d));
        this.querySelectorAll('.moment-space-card').forEach(card=>this.updateSpaceBudget(card));
      }
    }
  });
}
